import { useRef, useMemo } from "react";
import { useFrame, useLoader } from "@react-three/fiber";
import * as THREE from "three";
import SimplexNoise from "simplex-noise";
import bladeDiffuse from "/playground/blade_diffuse.jpg";
import bladeAlpha from "/playground/blade_alpha.jpg";
import { getVertexSource, fragmentSource } from "./shaders/shaders";

const simplex = new SimplexNoise(Math.random);

export default function Grass({ bladeOptions = { width: 0.05, height: 0.5, joints: 2 }, width = 50, instances = 30000 }) {
  const materialRef = useRef();
  const meshRef = useRef();
  const [diffuse, alpha] = useLoader(THREE.TextureLoader, [bladeDiffuse, bladeAlpha]);

  const { geometry, attributes } = useMemo(() => {
    const baseGeo = new THREE.PlaneGeometry(
      bladeOptions.width,
      bladeOptions.height,
      1,
      bladeOptions.joints
    );
    baseGeo.translate(0, bladeOptions.height / 2, 0);

    const instGeo = new THREE.InstancedBufferGeometry().copy(baseGeo);
    baseGeo.dispose(); // clean up original

    const attribData = getAttributeData(instances, width);

    instGeo.setAttribute("offset", new THREE.InstancedBufferAttribute(new Float32Array(attribData.offsets), 3));
    instGeo.setAttribute("orientation", new THREE.InstancedBufferAttribute(new Float32Array(attribData.orientations), 4));
    instGeo.setAttribute("stretch", new THREE.InstancedBufferAttribute(new Float32Array(attribData.stretches), 1));
    instGeo.setAttribute("halfRootAngleSin", new THREE.InstancedBufferAttribute(new Float32Array(attribData.halfRootAngleSin), 1));
    instGeo.setAttribute("halfRootAngleCos", new THREE.InstancedBufferAttribute(new Float32Array(attribData.halfRootAngleCos), 1));

    return { geometry: instGeo, attributes: attribData };
  }, [bladeOptions, width, instances]);

  useFrame((state) => {
    if (materialRef.current) {
      materialRef.current.uniforms.time.value = state.clock.elapsedTime / 4;
    }
  });

  return (
    <instancedMesh ref={meshRef} geometry={geometry} args={[null, null, instances]}>
      <rawShaderMaterial
        ref={materialRef}
        args={[{
          uniforms: {
            map: { value: diffuse },
            alphaMap: { value: alpha },
            time: { value: 0 }
          },
          vertexShader: getVertexSource(bladeOptions.height),
          fragmentShader: fragmentSource,
          side: THREE.DoubleSide,
          transparent: true
        }]}
      />
    </instancedMesh>
  );
}

function getAttributeData(instances, width) {
  const offsets = [], orientations = [], stretches = [], halfRootAngleSin = [], halfRootAngleCos = [];
  const q0 = new THREE.Quaternion(), q1 = new THREE.Quaternion();
  const min = -0.25, max = 0.25;

  for (let i = 0; i < instances; i++) {
    const x = Math.random() * width - width / 2;
    const z = Math.random() * width - width / 2;
    const y = getYPosition(x, z);
    offsets.push(x, y, z);

    let angle = Math.random() * Math.PI * 2;
    halfRootAngleSin.push(Math.sin(0.5 * angle));
    halfRootAngleCos.push(Math.cos(0.5 * angle));

    q0.setFromAxisAngle(new THREE.Vector3(0, 1, 0), angle);
    angle = Math.random() * (max - min) + min;
    q1.setFromAxisAngle(new THREE.Vector3(1, 0, 0), angle);
    q0.multiply(q1);
    angle = Math.random() * (max - min) + min;
    q1.setFromAxisAngle(new THREE.Vector3(0, 0, 1), angle);
    q0.multiply(q1);

    orientations.push(q0.x, q0.y, q0.z, q0.w);
    stretches.push(i < instances / 3 ? Math.random() * 1.8 : Math.random());
  }

  return {
    offsets,
    orientations,
    stretches,
    halfRootAngleSin,
    halfRootAngleCos
  };
}

function getYPosition(x, z) {
  return 3;
}
